import { ConnectIcon } from "@minket/brand/icons/connect";
import { MailboxesIcon } from "@minket/brand/icons/mailboxes";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { StatusBadge } from "@minket/ui/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { type ReactNode, Suspense, useState } from "react";
import { GmailMark } from "@/components/brands/gmail";
import { MicrosoftMark } from "@/components/brands/microsoft";
import { BrandName, markSize } from "@/components/brands/named";
import { stamp } from "@/components/domains/format";
import {
  ConnectMailboxDialog,
  type ConnectState,
  type SmtpBody,
} from "@/components/mailboxes/dialogs";
import {
  type Mailbox,
  type MailboxProvider,
  mailboxStatus,
  providerLabels,
  readCallback,
  returnUrl,
} from "@/components/mailboxes/model";
import { UnavailableAction } from "@/components/page/action";
import { FadeText } from "@/components/page/fade-text";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { Seams } from "@/components/page/seams";
import { Notice, QueryView } from "@/components/page/states";
import { MailboxTestAction } from "@/components/sending/actions";
import { describeError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/mailboxes")({
  component: Mailboxes,
});

const columns = ["Mailbox", "Status", "Sent copies", "Last used, UTC"] as const;
const order: readonly MailboxProvider[] = ["gmail", "microsoft", "smtp"];
const providerMarks: Record<MailboxProvider, ReactNode> = {
  gmail: <GmailMark className={markSize.gmail} />,
  microsoft: <MicrosoftMark className={markSize.microsoft} />,
  smtp: null,
};
const will = "Each connected mailbox will be listed here by provider, with its state.";

function standing(items: readonly Mailbox[]): string {
  const waiting = items.filter((item) => item.status === "needs_reconnect").length;
  const count = items.length === 1 ? "One mailbox" : `${items.length} mailboxes`;
  if (waiting > 0) {
    return `${count}; ${waiting === 1 ? "one needs" : `${waiting} need`} reconnecting, and mail sent through ${waiting === 1 ? "it" : "them"} fails until then.`;
  }
  return `${count}. Mail sent through one goes out as that person and lands in its Sent folder.`;
}

function Mailboxes() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const queryClient = useQueryClient();
  const list = useQuery({ ...apiQuery(endpoints.mailboxes, { projectId }), enabled: !!project });
  const [dialog, setDialog] = useState<ConnectState | null>(null);
  const [reconnecting, setReconnecting] = useState<string | null>(null);
  const search = useLocation({ select: (location) => location.search as Record<string, unknown> });
  const [callback] = useState(() => readCallback(search));
  const canManage = useCan("mailboxes.manage");
  const canSend = useCan("emails.send");
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.mailboxes, { projectId }) });

  const connect = useMutation({
    mutationFn: (body: { provider: "gmail" | "microsoft"; return_url: string } | SmtpBody) =>
      apiRequest(endpoints.connectMailbox, { projectId }, { body }),
    onSuccess: (result) => {
      if (result.object === "oauth") {
        window.location.assign(result.url);
        return;
      }
      refresh();
      setDialog(null);
      notify("success", `${result.address} connected.`);
    },
    onError: (error) => {
      setReconnecting(null);
      if (!dialog) notify("error", describeError(error).message);
    },
  });
  const remove = useMutation({
    mutationFn: (mailbox: Mailbox) =>
      apiRequest(endpoints.deleteMailbox, { projectId, mailboxId: mailbox.id }),
    onSuccess: (_, mailbox) => {
      refresh();
      notify("success", `${mailbox.address} removed.`);
    },
    onError: (error) => notify("error", describeError(error).message),
  });
  const oauth = (provider: "gmail" | "microsoft") =>
    connect.mutate({ provider, return_url: returnUrl("/mailboxes") });
  const reconnect = (mailbox: Mailbox) => {
    connect.reset();
    if (mailbox.provider === "smtp") {
      setDialog({ start: "smtp", address: mailbox.address });
      return;
    }
    // In place: the row says it is reconnecting while the provider's consent screen opens.
    setReconnecting(mailbox.id);
    oauth(mailbox.provider);
  };
  const preview = (busy: boolean) => <GhostTable columns={columns} label="mailboxes" busy={busy} />;

  return (
    <Page
      title="Mailboxes"
      description={list.data ? standing(list.data.data) : undefined}
      action={
        !project || !canManage ? (
          <UnavailableAction
            label="Connect mailbox"
            icon={ConnectIcon}
            reason={project ? "Viewers cannot connect mailboxes" : "Pick a project first"}
          />
        ) : (
          <Button
            onClick={() => {
              connect.reset();
              setDialog({ start: "choose" });
            }}
          >
            <ConnectIcon className="size-5" />
            Connect mailbox
          </Button>
        )
      }
    >
      {callback ? (
        <div role="status">
          <Notice tone={callback.tone}>{callback.message}</Notice>
        </div>
      ) : null}
      <ProjectScope will={will} icon={<MailboxesIcon />} preview={preview}>
        {() => (
          <QueryView query={list} noun="mailboxes" preview={preview}>
            {(page) =>
              page.data.length === 0 ? (
                <EmptyState
                  data-state="empty"
                  icon={<MailboxesIcon />}
                  title="No mailboxes connected"
                  description="Connect Gmail, Microsoft 365 or any SMTP mailbox to send as a person."
                  preview={preview(false)}
                />
              ) : (
                <MailboxLedger
                  projectId={projectId}
                  items={page.data}
                  canManage={canManage}
                  canSend={canSend}
                  reconnecting={reconnecting}
                  removing={remove.isPending ? (remove.variables?.id ?? null) : null}
                  onReconnect={reconnect}
                  onRemove={(mailbox) => remove.mutate(mailbox)}
                />
              )
            }
          </QueryView>
        )}
      </ProjectScope>
      {dialog ? (
        <Suspense fallback={null}>
          <ConnectMailboxDialog
            state={dialog}
            pending={connect.isPending}
            error={connect.error}
            onOauth={oauth}
            onSmtp={(body) => connect.mutate(body)}
            onClose={() => setDialog(null)}
          />
        </Suspense>
      ) : null}
    </Page>
  );
}

/**
 * The mailboxes by provider (direction §7): each with its state as a lamp and a word, the
 * provider's last error on one line that fades (its full text in the tooltip and its name), and
 * Reconnect in its row: OAuth mailboxes go to the provider's consent and come back here, an
 * SMTP one opens its form with the address filled in. A connected mailbox can send a test.
 */
function MailboxLedger({
  projectId,
  items,
  canManage,
  canSend,
  reconnecting,
  removing,
  onReconnect,
  onRemove,
}: {
  projectId: string;
  items: Mailbox[];
  canManage: boolean;
  canSend: boolean;
  reconnecting: string | null;
  removing: string | null;
  onReconnect: (mailbox: Mailbox) => void;
  onRemove: (mailbox: Mailbox) => void;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);
  return (
    <Table aria-label="Mailboxes connected to this project" className="min-w-[44rem]">
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead key={column}>{column}</TableHead>
          ))}
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      {order.map((provider) => {
        const group = items.filter((item) => item.provider === provider);
        if (group.length === 0) return null;
        return (
          <TableBody key={provider}>
            <TableRow className="hover:bg-transparent">
              <th
                scope="rowgroup"
                colSpan={5}
                className="type-heading px-3 pt-5 pb-1.5 text-left text-[15px] whitespace-nowrap"
              >
                <BrandName mark={providerMarks[provider]}>{providerLabels[provider]}</BrandName>
              </th>
            </TableRow>
            {group.map((item) => {
              const status = mailboxStatus[item.status];
              return (
                <TableRow key={item.id} className="[&>td]:align-middle">
                  <TableCell>
                    <span className="type-data block font-semibold">
                      <Seams>{item.address}</Seams>
                    </span>
                    {item.display_name ? (
                      <span className="block text-[13px] text-muted-foreground">
                        {item.display_name}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="max-w-0 min-w-44">
                    <StatusBadge tone={status.tone}>
                      {reconnecting === item.id ? "Reconnecting" : status.label}
                    </StatusBadge>
                    {item.last_error ? (
                      <FadeText className="text-[13px] text-muted-foreground">
                        {item.last_error}
                      </FadeText>
                    ) : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {item.saves_sent_copy ? "Yes" : "No"}
                  </TableCell>
                  <TableCell className="type-data whitespace-nowrap text-muted-foreground">
                    {item.last_used_at ? stamp(item.last_used_at) : "Never"}
                  </TableCell>
                  <TableCell>
                    {canManage && confirming === item.id ? (
                      <span className="flex justify-end gap-1.5">
                        <Button
                          variant="destructive"
                          size="sm"
                          loading={removing === item.id}
                          onClick={() => onRemove(item)}
                        >
                          Remove now
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                          Keep
                        </Button>
                      </span>
                    ) : (
                      <span className="flex justify-end gap-1.5">
                        {canSend && item.status === "connected" ? (
                          <MailboxTestAction projectId={projectId} mailbox={item} />
                        ) : null}
                        {canManage && item.status !== "connected" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            loading={reconnecting === item.id}
                            onClick={() => onReconnect(item)}
                          >
                            Reconnect <span className="sr-only">{item.address}</span>
                          </Button>
                        ) : null}
                        {canManage ? (
                          <Button variant="ghost" size="sm" onClick={() => setConfirming(item.id)}>
                            Remove <span className="sr-only">{item.address}</span>
                          </Button>
                        ) : null}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        );
      })}
    </Table>
  );
}
